import {
  Dialog,
  type ExportDataAsExcelParams,
  FormGroup,
  RadioButton,
  useDisclosureWithContext,
  useIntl,
  useWLOrgConfigContext,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';

export type ExportRowsModalContext = {
  onExport: (params: ExportDataAsExcelParams) => void;
};

const messages = defineMessages({
  export: {
    defaultMessage: 'Export',
    id: 'ExportRowsModal.export',
  },
  dateTimeZone: {
    defaultMessage: 'Date/Time Zone',
    id: 'ExportRowsModal.dateTimeZone',
  },
  local: {
    defaultMessage: 'Local',
    id: 'ExportRowsModal.local',
  },
});

export function ExportRowsModal({
  modalProps,
  title,
}: {
  modalProps: ReturnType<typeof useDisclosureWithContext<ExportRowsModalContext>>;
  title: string;
}) {
  const [dateExportType, setDateExportType] = useState<'UTC' | 'Local'>('UTC');
  const intl = useIntl();

  return (
    <Dialog
      {...modalProps}
      usePortal
      onConfirm={() => {
        modalProps.context?.onExport({ exportDatesRaw: dateExportType === 'UTC' });
      }}
      confirmLabel={intl.formatMessage(messages.export)}
      title={title}
    >
      <FormGroup
        gap="spacing8"
        display="flex"
        flexDirection="column"
        data-testid="utc"
        label={intl.formatMessage(messages.dateTimeZone)}
      >
        <RadioButton id="UTC" checked={dateExportType === 'UTC'} name="UTC" onChange={() => setDateExportType('UTC')}>
          UTC
        </RadioButton>
        <RadioButton
          id="Local"
          data-testid="local"
          checked={dateExportType === 'Local'}
          name="Local"
          onChange={() => setDateExportType('Local')}
        >
          {intl.formatMessage(messages.local)}
        </RadioButton>
      </FormGroup>
    </Dialog>
  );
}

export function useExportRowsAction(onExport: ExportRowsModalContext['onExport']) {
  // check whitelabel config, if we should show the modal or not
  const modal = useDisclosureWithContext<ExportRowsModalContext>();
  const { config } = useWLOrgConfigContext();
  const onClickExport = useMemo(() => {
    if (config.allowUTCDateExport) {
      return () => {
        modal.open({ onExport });
      };
    }
    return () => {
      onExport({ exportDatesRaw: false });
    };
  }, [config.allowUTCDateExport, modal, onExport]);

  return {
    onClickExport,
    modal,
  };
}
